import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  VisibilityOffOutlined,
  ReplayOutlined,
  CampaignOutlined,
} from '@mui/icons-material';
import { api } from '../../helpers/apiUtils';
import { Abschnitt, Feld } from '../../components/marketingForm';
import { KENNZEICHEN_LIVE } from './texteLive';
import AutoKarte, { AutoKarteDaten } from './AutoKarte';

/**
 * Einblendungen auf Abruf (Plan 2026-10-28, Live-Admin Paket 1).
 *
 * Bibliothek vorbereiteter Angebote. Während einer laufenden Sendung blendet Carsten (oder Felix) eines mit einem
 * Klick ein: auf live.finanztrends.de als Karte direkt unter dem Video, in Einbindungen als Knopf auf dem Bild.
 * Es verschwindet nach der eingestellten Dauer von selbst. Jeder Zuschauer sieht es ab dem Moment, in dem der
 * Klick in SEINEM Bild ankommt (Player: pruefeEinblendung).
 *
 * Regeln der Marketing-Oberfläche (.claude/docs/webinar-ui-marketing.md): jedes Feld mit Kennzeichen, ein Satz je
 * Feld, keine Fachbegriffe. Fehlertexte des Servers (`details`) stehen wörtlich da.
 */

interface EinblItem {
  id: number;
  headline: string;
  body_text: string | null;
  button_label: string;
  button_url: string;
  duration_sec: number;
  archived: boolean | number;
  sort_order: number;
  clicks_today: number;
  updated_at: string;
  /** Beginn des letzten Einblendens (echte Epoche ms) oder null = noch nie. */
  last_shown_ms?: number | null;
}

interface AktiverLauf {
  run_id: number;
  cta_id: number;
  headline: string;
  button_label: string;
  start_ms: number;
  end_ms: number;
  server_now_ms: number;
}

interface ListeDaten {
  items: EinblItem[];
  active_run: AktiverLauf | null;
  show_running: boolean;
  show_hint: string | null;
}

interface Entwurf {
  id: number | null;
  headline: string;
  body_text: string;
  button_label: string;
  button_url: string;
  duration_sec: number;
}

// Kennzeichen-Tooltips: die Live-Texte, nur „Pflicht“ spricht hier von der Einblendung statt der Sendung
// (KENNZEICHEN_LIVE.pflicht sagt „…lässt sich die Sendung nicht speichern“ — für diesen Dialog falsch).
const KENNZEICHEN_EINBLENDUNG = {
  ...KENNZEICHEN_LIVE,
  pflicht: 'Ohne dieses Feld lässt sich die Einblendung nicht speichern.',
};

const POLL_MS = 5000;
const MAX_UEBERSCHRIFT = 120;
const MAX_KURZTEXT = 280;
const MAX_KNOPF = 60;
const STANDARD_DAUER = 300;
const DAUER_OPTIONEN = [30, 60, 90, 120, 180, 240, 300, 420, 600];

const LEER: Entwurf = {
  id: null,
  headline: '',
  body_text: '',
  button_label: '',
  button_url: '',
  duration_sec: STANDARD_DAUER,
};

/** 180 → „3 Min“, 30 → „30 Sek“, 90 → „1:30 Min“. */
export function dauerText(sek: number): string {
  const s = Math.max(0, Math.round(sek));
  if (s < 60) return `${s} Sek`;
  if (s % 60 === 0) return `${s / 60} Min`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} Min`;
}

const BERLIN_TAG = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const BERLIN_UHR = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * „heute, 10:12 Uhr“ / „gestern, 10:12 Uhr“ / „22.10.2026, 10:12 Uhr“ — Berliner Zeit, aus echter Epoche.
 * Paket 2 (E2): damit niemand ein Angebot doppelt anlegt, weil er nicht sieht, dass es es schon gibt.
 */
export function zuletztText(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return 'Noch nie eingeblendet';
  const tag = BERLIN_TAG.format(new Date(ms));
  const uhr = BERLIN_UHR.format(new Date(ms));
  const heute = BERLIN_TAG.format(new Date());
  const gestern = BERLIN_TAG.format(new Date(Date.now() - 24 * 3600 * 1000));
  const wann = tag === heute ? 'heute' : tag === gestern ? 'gestern' : tag;
  return `Zuletzt eingeblendet: ${wann}, ${uhr} Uhr`;
}

/** Restzeit m:ss. */
function restText(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function fehlerText(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { error?: string; details?: unknown } };
    message?: string;
  };
  const data = e?.response?.data;
  if (typeof data?.details === 'string' && data.details) return data.details;
  if (data?.error) return data.error;
  return e?.message || fallback;
}

/** Fehler der Eingabe, bevor gespeichert wird. Der Server prüft dasselbe noch einmal am gespeicherten Wert. */
function pruefeEntwurf(e: Entwurf): Partial<Record<keyof Entwurf, string>> {
  const f: Partial<Record<keyof Entwurf, string>> = {};
  if (!e.headline.trim()) f.headline = 'Bitte eine Überschrift eintragen.';
  else if (e.headline.trim().length > MAX_UEBERSCHRIFT)
    f.headline = `Höchstens ${MAX_UEBERSCHRIFT} Zeichen.`;
  if (e.body_text.trim().length > MAX_KURZTEXT) f.body_text = `Höchstens ${MAX_KURZTEXT} Zeichen.`;
  if (!e.button_label.trim()) f.button_label = 'Bitte einen Knopf-Text eintragen.';
  else if (e.button_label.trim().length > MAX_KNOPF)
    f.button_label = `Höchstens ${MAX_KNOPF} Zeichen.`;
  const url = e.button_url.trim();
  if (!url) f.button_url = 'Bitte den Ziel-Link eintragen.';
  else if (!/^https:\/\//i.test(url)) f.button_url = 'Der Link muss mit https:// beginnen.';
  else {
    try {
      // eslint-disable-next-line no-new
      new URL(url);
    } catch {
      f.button_url = 'Das ist kein gültiger Link.';
    }
  }
  return f;
}

/** Nachbau der Karte im Player (index.html `.einbl`) — gleiche Farben, gleiche Reihenfolge. */
const BannerVorschau: FC<{ headline: string; body: string; knopf: string; schmal: boolean }> = ({
  headline,
  body,
  knopf,
  schmal,
}) => (
  <Box sx={{ bgcolor: '#0C0F13', borderRadius: 2, p: schmal ? 1 : 1.5 }}>
    <Box
      sx={{
        height: schmal ? 56 : 72,
        borderRadius: 1.5,
        bgcolor: '#000',
        color: '#93A0AD',
        fontSize: 12,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        pb: 1,
        mb: 1.25,
      }}
    >
      … Unterkante des Videos …
    </Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: schmal ? 'minmax(0,1fr)' : 'minmax(0,1fr) auto',
        alignItems: 'center',
        gap: schmal ? '10px' : '10px 24px',
        bgcolor: '#ffffff',
        color: '#141C22',
        borderRadius: '10px',
        borderLeft: '4px solid #FF6A45',
        p: schmal ? '14px 14px 14px 16px' : '16px 18px 16px 20px',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#5B6772',
            mb: 0.5,
          }}
        >
          Anzeige
        </Box>
        <Box
          sx={{
            fontSize: schmal ? 18 : 20,
            lineHeight: 1.25,
            fontWeight: 800,
            overflowWrap: 'anywhere',
            color: headline ? '#141C22' : '#9aa3ad',
          }}
        >
          {headline || 'Hier steht die Überschrift'}
        </Box>
        {body && (
          <Box
            sx={{
              mt: 0.5,
              fontSize: schmal ? 14 : 15,
              lineHeight: 1.4,
              color: '#5B6772',
              overflowWrap: 'anywhere',
            }}
          >
            {body}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          minHeight: 48,
          px: '22px',
          py: '12px',
          borderRadius: '8px',
          bgcolor: '#477DEA',
          color: '#0C0F13',
          fontSize: 16,
          fontWeight: 800,
          lineHeight: 1.2,
          maxWidth: schmal ? 'none' : 320,
          overflowWrap: 'anywhere',
        }}
      >
        {knopf || 'Knopf-Text'}
      </Box>
    </Box>
  </Box>
);

/** Was „Sendung beenden“ im Sendepult über eine gerade laufende Einblendung sagen muss. */
export interface LaufendeEinblendung {
  headline: string;
}

// Bei Zuschauern verschwindet eine ausgeblendete Einblendung erst, wenn ihr Bild die Stopp-Marke erreicht
// (liveOnDemandCta.stoppeEinblendungen: Klick + Schnitt-Offset; Player index.html pruefeEinblendung rechnet
// gegen die Bildzeit, Standard-Rückstand EINBL_STANDARD_RUECKSTAND_MS = 25 s).
const AUSBLENDEN_WIRKUNG =
  'Bei Zuschauern verschwindet es mit ihrer Bildverzögerung (meist 20–30 Sekunden) — so, wie es auch erschienen ist.';

const Einblendungen: FC<{ onLaufAenderung?: (lauf: LaufendeEinblendung | null) => void }> = ({
  onLaufAenderung,
}) => {
  const theme = useTheme();
  const schmal = useMediaQuery(theme.breakpoints.down('sm'));
  // Handy und Tablet: Karten kompakt (Überschrift + Knopf), der Rest auf Antippen.
  const kompakt = useMediaQuery(theme.breakpoints.down('md'));
  const [offeneDetails, setOffeneDetails] = useState<number[]>([]);
  const [daten, setDaten] = useState<ListeDaten | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<{
    text: string;
    art: 'success' | 'error' | 'info';
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Uhrabgleich: Serverzeit − Browserzeit beim letzten Abruf. Der Countdown rechnet gegen die Serveruhr.
  const skewMs = useRef(0);
  const [, setTick] = useState(0);

  const [archivAuf, setArchivAuf] = useState(false);
  const [archiv, setArchiv] = useState<EinblItem[] | null>(null);

  const [dialogOffen, setDialogOffen] = useState(false);
  const [entwurf, setEntwurf] = useState<Entwurf>(LEER);
  const [gezeigteFehler, setGezeigteFehler] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [dialogFehler, setDialogFehler] = useState<string | null>(null);
  const [autoKarte, setAutoKarte] = useState<AutoKarteDaten | null>(null);

  const laden = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data: ListeDaten }>(
        '/api/live/admin/on-demand-ctas',
        { params: { archived: 0 } },
      );
      const d = res.data.data;
      if (d.active_run && Number.isFinite(d.active_run.server_now_ms)) {
        skewMs.current = d.active_run.server_now_ms - Date.now();
      }
      setDaten(d);
      setLadeFehler(null);
    } catch (err) {
      setLadeFehler(fehlerText(err, 'Einblendungen konnten nicht geladen werden.'));
    }
  }, []);

  const ladeArchiv = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data: ListeDaten }>(
        '/api/live/admin/on-demand-ctas',
        { params: { archived: 1 } },
      );
      setArchiv(res.data.data.items || []);
    } catch (err) {
      setMeldung({ text: fehlerText(err, 'Archiv konnte nicht geladen werden.'), art: 'error' });
    }
  }, []);

  // Alle 5 s nachladen, solange der Reiter sichtbar ist (die Karte existiert nur im Reiter Live-Steuerung).
  useEffect(() => {
    laden();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') laden();
    }, POLL_MS);
    const beiSichtbar = () => {
      if (document.visibilityState === 'visible') laden();
    };
    document.addEventListener('visibilitychange', beiSichtbar);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', beiSichtbar);
    };
  }, [laden]);

  useEffect(() => {
    if (archivAuf) ladeArchiv();
  }, [archivAuf, ladeArchiv]);

  const lauf = daten?.active_run ?? null;
  const restMs = lauf ? lauf.end_ms - (Date.now() + skewMs.current) : 0;
  const laeuft = !!lauf && restMs > 0;

  // Sekundentakt nur, solange eine Einblendung läuft.
  useEffect(() => {
    if (!laeuft) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [laeuft]);

  // Das Sendepult nennt in „Sendung beenden“ die laufende Einblendung. Ref statt Abhängigkeit: der
  // Aufrufer übergibt eine neue Funktion je Render.
  const onLaufAenderungRef = useRef(onLaufAenderung);
  onLaufAenderungRef.current = onLaufAenderung;
  const laufendeUeberschrift = laeuft && lauf ? lauf.headline : null;
  useEffect(() => {
    onLaufAenderungRef.current?.(
      laufendeUeberschrift != null ? { headline: laufendeUeberschrift } : null,
    );
  }, [laufendeUeberschrift]);

  const aktion = async (
    schluessel: string,
    fn: () => Promise<unknown>,
    erfolg: string | null,
    fallback: string,
  ) => {
    setBusy(schluessel);
    try {
      await fn();
      if (erfolg) setMeldung({ text: erfolg, art: 'success' });
      await laden();
      if (archivAuf) await ladeArchiv();
    } catch (err) {
      setMeldung({ text: fehlerText(err, fallback), art: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const einblenden = (item: EinblItem, dauerSek?: number) =>
    aktion(
      `show-${item.id}`,
      async () => {
        const res = await api.post<{ success: boolean; data: AktiverLauf }>(
          `/api/live/admin/on-demand-ctas/${item.id}/show`,
          { duration_sec: dauerSek ?? item.duration_sec },
        );
        const neu = res.data.data;
        if (neu && Number.isFinite(neu.server_now_ms))
          skewMs.current = neu.server_now_ms - Date.now();
        if (neu) setDaten((d) => (d ? { ...d, active_run: neu } : d));
      },
      null,
      'Einblenden hat nicht geklappt.',
    );

  const ausblenden = () =>
    aktion(
      'stop',
      async () => {
        await api.post('/api/live/admin/on-demand-ctas/stop');
        setDaten((d) => (d ? { ...d, active_run: null } : d));
      },
      `Ausgeblendet. ${AUSBLENDEN_WIRKUNG}`,
      'Ausblenden hat nicht geklappt.',
    );

  const archivieren = (item: EinblItem) =>
    aktion(
      `archive-${item.id}`,
      () => api.post(`/api/live/admin/on-demand-ctas/${item.id}/archive`),
      `„${item.headline}“ liegt jetzt im Archiv.`,
      'Archivieren hat nicht geklappt.',
    );

  const wiederherstellen = (item: EinblItem) =>
    aktion(
      `unarchive-${item.id}`,
      () => api.post(`/api/live/admin/on-demand-ctas/${item.id}/unarchive`),
      `„${item.headline}“ ist wieder in der Liste.`,
      'Wiederherstellen hat nicht geklappt.',
    );

  const oeffneNeu = () => {
    setEntwurf(LEER);
    setGezeigteFehler(false);
    setDialogFehler(null);
    setDialogOffen(true);
    // Für den Hinweis „gibt es schon“ auch das Archiv kennen.
    if (archiv == null) ladeArchiv();
  };

  const oeffneBearbeiten = (item: EinblItem) => {
    setEntwurf({
      id: item.id,
      headline: item.headline,
      body_text: item.body_text ?? '',
      button_label: item.button_label,
      button_url: item.button_url,
      duration_sec: item.duration_sec || STANDARD_DAUER,
    });
    setGezeigteFehler(false);
    setDialogFehler(null);
    setDialogOffen(true);
  };

  const fehler = pruefeEntwurf(entwurf);
  const hatFehler = Object.keys(fehler).length > 0;

  const speichern = async () => {
    setGezeigteFehler(true);
    if (hatFehler) return;
    setSpeichert(true);
    setDialogFehler(null);
    const body = {
      headline: entwurf.headline.trim(),
      body_text: entwurf.body_text.trim() || null,
      button_label: entwurf.button_label.trim(),
      button_url: entwurf.button_url.trim(),
      duration_sec: entwurf.duration_sec,
    };
    try {
      if (entwurf.id == null) await api.post('/api/live/admin/on-demand-ctas', body);
      else await api.patch(`/api/live/admin/on-demand-ctas/${entwurf.id}`, body);
      setDialogOffen(false);
      setMeldung({
        text: entwurf.id == null ? 'Einblendung angelegt.' : 'Änderung gespeichert.',
        art: 'success',
      });
      await laden();
    } catch (err) {
      setDialogFehler(fehlerText(err, 'Speichern hat nicht geklappt.'));
    } finally {
      setSpeichert(false);
    }
  };

  const items = daten?.items ?? [];
  const sendungLaeuft = !!daten?.show_running;
  const laufItem = lauf ? items.find((i) => i.id === lauf.cta_id) : undefined;
  const nochmalSek =
    laufItem?.duration_sec ??
    (lauf ? Math.max(30, Math.round((lauf.end_ms - lauf.start_ms) / 1000)) : STANDARD_DAUER);
  const dauerOptionen = DAUER_OPTIONEN.includes(entwurf.duration_sec)
    ? DAUER_OPTIONEN
    : [...DAUER_OPTIONEN, entwurf.duration_sec].sort((a, b) => a - b);

  const feldFehler = (k: keyof Entwurf) => (gezeigteFehler ? fehler[k] : undefined);

  // R2 (9): gleiche Überschrift schon vorhanden? Groß-/Kleinschreibung und Ränder egal.
  const vergleichsUeberschrift = entwurf.headline.trim().toLocaleLowerCase('de-DE');
  const gleicheUeberschrift = vergleichsUeberschrift
    ? (items
        .map((i) => ({ item: i, imArchiv: false }))
        .concat((archiv ?? []).map((i) => ({ item: i, imArchiv: true })))
        .find(
          ({ item }) =>
            item.id !== entwurf.id &&
            item.headline.trim().toLocaleLowerCase('de-DE') === vergleichsUeberschrift,
        ) ?? null)
    : null;

  // Neu starten: dieselbe Einblendung noch einmal die volle Zeit (ersetzt den laufenden Lauf).
  const neuStarten = () => {
    if (!lauf) return;
    // Die Karte hat ihren eigenen Weg: dort wird frisch aufgelöst, ein alter Text kann nicht zurückkommen.
    if (autoKarte && autoKarte.cta_id === lauf.cta_id) {
      aktion(
        `show-${lauf.cta_id}`,
        () =>
          api.post('/api/live/admin/auto-karte/show', {
            kind: autoKarte.kind,
            event_id: autoKarte.event_id,
            duration_sec: nochmalSek,
          }),
        null,
        'Einblenden hat nicht geklappt.',
      );
      return;
    }
    const it = laufItem ?? items.find((i) => i.id === lauf.cta_id);
    if (it) {
      einblenden(it, nochmalSek);
      return;
    }
    aktion(
      `show-${lauf.cta_id}`,
      () =>
        api.post(`/api/live/admin/on-demand-ctas/${lauf.cta_id}/show`, {
          duration_sec: nochmalSek,
        }),
      null,
      'Einblenden hat nicht geklappt.',
    );
  };

  const hinweisBasis = daten?.show_hint || 'Einblenden geht nur während einer laufenden Sendung.';
  // Der Server-Hinweis sagt das Vorbereiten teils schon selbst (liveOnDemandCta.sendungsLage) — nicht doppeln.
  const sendungsHinweis = /vorbereit/i.test(hinweisBasis)
    ? hinweisBasis
    : `${hinweisBasis} Vorbereiten kannst du die Einblendungen jederzeit.`;

  const toggleDetails = (id: number) =>
    setOffeneDetails((alt) => (alt.includes(id) ? alt.filter((x) => x !== id) : alt.concat(id)));

  return (
    <Abschnitt
      id="einblendungen"
      titel="Einblendungen"
      erklaerung="Vorbereitete Angebote, die du während der Sendung mit einem Klick direkt unter dem Video einblendest. Sie verschwinden nach der eingestellten Zeit von selbst."
      kopfRechts={
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={oeffneNeu}>
          Neue Einblendung
        </Button>
      }
    >
      {ladeFehler && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {ladeFehler}
        </Alert>
      )}
      {meldung && (
        <Alert severity={meldung.art} onClose={() => setMeldung(null)} sx={{ mb: 1.5 }}>
          {meldung.text}
        </Alert>
      )}

      {daten && !sendungLaeuft && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {sendungsHinweis}
        </Alert>
      )}

      {lauf && laeuft && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            border: '1px solid #b7e4c7',
            bgcolor: '#e6f5ec',
          }}
        >
          <Typography sx={{ fontWeight: 700, color: '#0A6634', overflowWrap: 'anywhere' }}>
            Läuft: {lauf.headline} — noch {restText(restMs)}
          </Typography>
          {/* R3 (4): Selbsthilfe, wenn Carsten nichts sieht — Player in neuem Tab. */}
          <Typography variant="body2" sx={{ mt: 0.5, color: '#0A6634' }}>
            Zuschauer sehen alles mit 20–30 s Verzögerung. Nichts zu sehen?{' '}
            <MuiLink
              href="https://live.finanztrends.de/"
              target="_blank"
              rel="noopener"
              underline="always"
              sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Player ansehen ↗
            </MuiLink>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            „Ausblenden“ nimmt es heraus, „Neu starten“ zeigt es noch einmal von vorn, für die volle
            Dauer. Ein Klick auf eine andere Einblendung ersetzt diese. Endet die Sendung,
            verschwindet es mit.
          </Typography>
          {/* R3 (3): dieselbe Reihenfolge und Gewichtung wie an der laufenden Karte — Ausblenden zuerst
              und als Hauptknopf, Neu starten danach als Nebenknopf. */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.25 }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<VisibilityOffOutlined />}
              onClick={ausblenden}
              disabled={busy != null}
              sx={{ fontWeight: 700 }}
            >
              {busy === 'stop' ? <CircularProgress size={18} color="inherit" /> : 'Ausblenden'}
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<ReplayOutlined />}
              disabled={busy != null || !sendungLaeuft}
              onClick={neuStarten}
            >
              Neu starten · {dauerText(nochmalSek)}
            </Button>
          </Stack>
        </Box>
      )}

      {/* Bewusst vor dem Lade-Zustand der Liste: die Karte hat einen eigenen Abruf und hängt nicht an ihr. */}
      <AutoKarte
        sendungLaeuft={sendungLaeuft}
        aktiveCtaId={laeuft && lauf ? lauf.cta_id : null}
        restText={laeuft ? restText(restMs) : null}
        dauerText={dauerText}
        zuletztText={zuletztText}
        onGeaendert={laden}
        onAusblenden={ausblenden}
        ausblendenBusy={busy === 'stop'}
        onKarte={setAutoKarte}
      />

      {!daten && !ladeFehler && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={22} />
        </Box>
      )}

      {daten && items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Noch keine eigene Einblendung angelegt. Mit „Neue Einblendung“ bereitest du ein weiteres
          Angebot vor.
        </Typography>
      )}

      {/* Am Handy mehr Abstand: „Ausblenden" der laufenden Karte stand direkt über „Einblenden" der nächsten. */}
      <Stack spacing={{ xs: 2, md: 1.25 }}>
        {items.map((item) => {
          const istAktiv = laeuft && lauf?.cta_id === item.id;
          const detailsOffen = !kompakt || offeneDetails.includes(item.id);
          // R2 (5): die laufende Karte trägt ihre Aktionen selbst, nicht nur der grüne Kasten oben.
          // R3 (3): Reihenfolge und Gewichtung wie im grünen Kasten — Ausblenden zuerst und als Hauptknopf.
          const aktionen = istAktiv ? (
            <Stack spacing={0.75} sx={{ minWidth: { md: 220 } }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: '#0A6634', textAlign: { md: 'center' } }}
              >
                Läuft · noch {restText(restMs)}
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="large"
                startIcon={<VisibilityOffOutlined />}
                disabled={busy != null}
                onClick={ausblenden}
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {busy === 'stop' ? <CircularProgress size={18} color="inherit" /> : 'Ausblenden'}
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<ReplayOutlined />}
                disabled={busy != null || !sendungLaeuft}
                onClick={neuStarten}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {busy === `show-${item.id}` ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  `Neu starten · ${dauerText(nochmalSek)}`
                )}
              </Button>
            </Stack>
          ) : (
            <Box sx={{ minWidth: { md: 220 } }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<CampaignOutlined />}
                color="primary"
                disabled={!sendungLaeuft || busy != null}
                onClick={() => einblenden(item)}
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {busy === `show-${item.id}` ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  `Einblenden · ${dauerText(item.duration_sec)}`
                )}
              </Button>
            </Box>
          );
          const ueberschrift = (
            <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
              {item.headline}
            </Typography>
          );
          // R2 (8): Bearbeiten/Archivieren klein ans Kartenende, weit weg vom Einblenden-Knopf.
          const details = (
            <>
              {item.body_text && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflowWrap: 'anywhere',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {item.body_text}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ mt: 0.5, overflowWrap: 'anywhere' }}
              >
                Knopf „{item.button_label}“ → {item.button_url}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                Klicks heute: <strong>{item.clicks_today ?? 0}</strong> · Dauer{' '}
                {dauerText(item.duration_sec)}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={{ color: item.last_shown_ms ? 'text.secondary' : '#1e6b52' }}
              >
                {zuletztText(item.last_shown_ms)}
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                <Button
                  size="small"
                  onClick={() => oeffneBearbeiten(item)}
                  disabled={busy != null}
                  sx={{ minWidth: 0, px: 1, fontSize: 12 }}
                >
                  Bearbeiten
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => archivieren(item)}
                  disabled={busy != null || istAktiv}
                  title={istAktiv ? 'Erst ausblenden, dann archivieren.' : undefined}
                  sx={{ minWidth: 0, px: 1, fontSize: 12, color: 'text.secondary' }}
                >
                  Archivieren
                </Button>
              </Stack>
            </>
          );
          return (
            <Paper
              key={item.id}
              variant="outlined"
              sx={{
                p: 1.5,
                borderColor: istAktiv ? '#0A6634' : '#e5e7eb',
                borderWidth: istAktiv ? 2 : 1,
              }}
            >
              {kompakt ? (
                <Stack spacing={1}>
                  {ueberschrift}
                  {aktionen}
                  <Box>
                    <Button
                      size="small"
                      color="inherit"
                      endIcon={
                        <ExpandMoreIcon
                          sx={{ transform: detailsOffen ? 'rotate(180deg)' : 'none' }}
                        />
                      }
                      onClick={() => toggleDetails(item.id)}
                      aria-expanded={detailsOffen}
                      sx={{ px: 0.5, fontSize: 12, color: 'text.secondary' }}
                    >
                      {detailsOffen ? 'Details ausblenden' : 'Details, Bearbeiten, Archivieren'}
                    </Button>
                    {detailsOffen && <Box sx={{ mt: 0.5 }}>{details}</Box>}
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {ueberschrift}
                    {details}
                  </Box>
                  {aktionen}
                </Stack>
              )}
            </Paper>
          );
        })}
      </Stack>
      <Accordion
        disableGutters
        elevation={0}
        expanded={archivAuf}
        onChange={(_, auf) => setArchivAuf(auf)}
        sx={{
          mt: 2,
          border: '1px dashed #d1d5db',
          borderRadius: '6px',
          '&:before': { display: 'none' },
          bgcolor: '#fcfcfd',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle2">Archiv</Typography>
            <Typography variant="caption" color="text.secondary">
              Archivierte Einblendungen erscheinen nicht in der Liste oben. Nichts wird gelöscht —
              „Wiederherstellen“ holt sie zurück.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {archiv == null && <CircularProgress size={18} />}
          {archiv != null && archiv.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Das Archiv ist leer.
            </Typography>
          )}
          <Stack spacing={1}>
            {(archiv ?? []).map((item) => (
              <Stack
                key={item.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ borderBottom: '1px solid #eef0f2', pb: 1 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {item.headline}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    Knopf „{item.button_label}“ · Dauer {dauerText(item.duration_sec)} ·{' '}
                    {zuletztText(item.last_shown_ms)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => wiederherstellen(item)}
                  disabled={busy != null}
                >
                  Wiederherstellen
                </Button>
              </Stack>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={dialogOffen}
        onClose={speichert ? undefined : () => setDialogOffen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={schmal}
      >
        <DialogTitle>
          {entwurf.id == null ? 'Neue Einblendung' : 'Einblendung bearbeiten'}
        </DialogTitle>
        <DialogContent dividers>
          <Feld
            titel="Überschrift"
            art="pflicht"
            kennzeichenErklaerung={KENNZEICHEN_EINBLENDUNG.pflicht}
            wofuer="Die große Zeile im Angebot unter dem Video — sagt in einem Satz, worum es geht."
          >
            <TextField
              fullWidth
              size="small"
              value={entwurf.headline}
              inputProps={{ 'aria-label': 'Überschrift' }}
              onChange={(e) => setEntwurf({ ...entwurf, headline: e.target.value })}
              placeholder="z. B. Heute 50 % Rabatt auf den Börsenbrief"
              error={!!feldFehler('headline')}
              helperText={
                feldFehler('headline') ??
                `${entwurf.headline.trim().length} / ${MAX_UEBERSCHRIFT} Zeichen`
              }
            />
            {/* R3 (8): sprachlich geglättet, mit Knopf zur vorhandenen Einblendung (nur beim Neuanlegen —
                beim Bearbeiten würde der Wechsel die eigenen Änderungen verwerfen). */}
            {gleicheUeberschrift && (
              <Alert
                severity="info"
                sx={{ mt: 1 }}
                action={
                  entwurf.id == null ? (
                    <Button
                      color="inherit"
                      size="small"
                      sx={{ whiteSpace: 'nowrap' }}
                      onClick={() => {
                        const vorhanden = gleicheUeberschrift.item;
                        if (gleicheUeberschrift.imArchiv) {
                          setDialogOffen(false);
                          wiederherstellen(vorhanden);
                        } else {
                          oeffneBearbeiten(vorhanden);
                        }
                      }}
                    >
                      {gleicheUeberschrift.imArchiv ? 'Wiederherstellen' : 'Vorhandene bearbeiten'}
                    </Button>
                  ) : undefined
                }
              >
                {entwurf.id == null
                  ? gleicheUeberschrift.imArchiv
                    ? 'Diese Überschrift gibt es schon im Archiv — besser die vorhandene Einblendung wiederherstellen und dann bearbeiten.'
                    : 'Diese Überschrift gibt es schon — besser die vorhandene Einblendung bearbeiten.'
                  : `Eine andere Einblendung${gleicheUeberschrift.imArchiv ? ' im Archiv' : ''} trägt schon diese Überschrift — zwei gleiche Karten sind in der Liste schwer zu unterscheiden.`}{' '}
                ({zuletztText(gleicheUeberschrift.item.last_shown_ms)})
              </Alert>
            )}
          </Feld>
          <Feld
            titel="Kurztext"
            art="optional"
            kennzeichenErklaerung={KENNZEICHEN_EINBLENDUNG.optional}
            wofuer="Ein bis zwei Sätze unter der Überschrift. Bleibt das Feld leer, steht nur die Überschrift da."
          >
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={entwurf.body_text}
              inputProps={{ 'aria-label': 'Kurztext' }}
              onChange={(e) => setEntwurf({ ...entwurf, body_text: e.target.value })}
              error={!!feldFehler('body_text')}
              helperText={
                feldFehler('body_text') ??
                `${entwurf.body_text.trim().length} / ${MAX_KURZTEXT} Zeichen`
              }
            />
          </Feld>
          <Feld
            titel="Knopf-Text"
            art="pflicht"
            kennzeichenErklaerung={KENNZEICHEN_EINBLENDUNG.pflicht}
            wofuer="Steht auf dem Knopf, den Zuschauer anklicken. Auf Partnerseiten, die den Player eingebaut haben, ist er das Einzige, was zu sehen ist."
          >
            <TextField
              fullWidth
              size="small"
              value={entwurf.button_label}
              inputProps={{ 'aria-label': 'Knopf-Text' }}
              onChange={(e) => setEntwurf({ ...entwurf, button_label: e.target.value })}
              placeholder="z. B. Jetzt Angebot sichern"
              error={!!feldFehler('button_label')}
              helperText={
                feldFehler('button_label') ??
                `${entwurf.button_label.trim().length} / ${MAX_KNOPF} Zeichen`
              }
            />
          </Feld>
          <Feld
            titel="Ziel-Link"
            art="pflicht"
            kennzeichenErklaerung={KENNZEICHEN_EINBLENDUNG.pflicht}
            wofuer="Hierhin führt der Knopf, in einem neuen Tab — die Sendung läuft weiter. Muss mit https:// beginnen."
          >
            <TextField
              fullWidth
              size="small"
              value={entwurf.button_url}
              inputProps={{ 'aria-label': 'Ziel-Link' }}
              onChange={(e) => setEntwurf({ ...entwurf, button_url: e.target.value })}
              placeholder="https://lp.finanztrends.de/…"
              error={!!feldFehler('button_url')}
              helperText={feldFehler('button_url')}
            />
          </Feld>
          <Feld
            titel="Anzeigedauer"
            art="optional"
            kennzeichenErklaerung={KENNZEICHEN_EINBLENDUNG.optional}
            wofuer="So lange bleibt das Angebot nach deinem Klick zu sehen, danach verschwindet es von selbst. Lässt du die Auswahl stehen, gilt der Standard: 5 Minuten."
          >
            <TextField
              select
              size="small"
              value={entwurf.duration_sec}
              SelectProps={{ inputProps: { 'aria-label': 'Anzeigedauer' } }}
              onChange={(e) => setEntwurf({ ...entwurf, duration_sec: Number(e.target.value) })}
              sx={{ minWidth: 180 }}
            >
              {dauerOptionen.map((s) => (
                <MenuItem key={s} value={s}>
                  {dauerText(s)}
                </MenuItem>
              ))}
            </TextField>
          </Feld>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, mb: 0.75 }}>
            Vorschau — so steht es auf live.finanztrends.de unter dem Video
          </Typography>
          <BannerVorschau
            headline={entwurf.headline.trim()}
            body={entwurf.body_text.trim()}
            knopf={entwurf.button_label.trim()}
            schmal={schmal}
          />
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
            Auf dem Handy steht der Knopf unter dem Text. Im Vollbild liegt das Angebot unten im
            Bild. Auf Partnerseiten, die den Player eingebaut haben, erscheint nur der Knopf auf dem
            Video.
          </Typography>

          {dialogFehler && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogFehler}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOffen(false)} disabled={speichert}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={speichern}
            disabled={speichert || (gezeigteFehler && hatFehler)}
            startIcon={speichert ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Abschnitt>
  );
};

export default Einblendungen;
